/**
 * shared/auth/permissions — Frontend permission primitives.
 *
 * Mirrors the backend `apps/rbac/roles.py` role/action mapping exactly
 * (default-deny). Exposes:
 *   - `roleCan(role, action)`          — pure helper, no React dependency
 *   - `useHasPerm(action)`             — React hook reading the active-org role
 *   - `<PermissionGuard action fallback>` — render-conditional component
 *
 * FSD boundary: `shared` may NOT import from `entities` or higher.
 * The active-org role is read via an injectable accessor seam — the same pattern
 * used by `shared/auth/token.ts` for the access token and by
 * `shared/api/middleware.ts` for the active-org ID.
 *
 * Wiring (performed by entities/organization/model/org-store.ts):
 *   import { setActiveRoleAccessor } from '@/shared/auth';
 *   setActiveRoleAccessor(() => useOrgStore.getState().activeOrg?.my_role ?? null);
 */

import { type ReactNode } from 'react';

// ---------------------------------------------------------------------------
// Role + action types
// ---------------------------------------------------------------------------

/** The three membership roles — mirrors backend RoleEnum. */
export type MemberRole = 'owner' | 'admin' | 'member';

/** All actions the permission system recognises. */
export type OrgAction =
  | 'org:view'
  | 'org:update'
  | 'org:delete'
  | 'member:view'
  | 'member:manage'
  | 'role:assign'
  | 'invite:view'
  | 'invite:create'
  | 'invite:revoke'
  | 'audit:view';

// ---------------------------------------------------------------------------
// Action → allowed-roles mapping (mirrors backend ACTION_ROLES exactly)
// ---------------------------------------------------------------------------

const ACTION_ROLES: Record<OrgAction, ReadonlyArray<MemberRole>> = {
  'org:view':      ['owner', 'admin', 'member'],
  'org:update':    ['owner', 'admin'],
  'org:delete':    ['owner'],
  'member:view':   ['owner', 'admin', 'member'],
  'member:manage': ['owner', 'admin'],
  'role:assign':   ['owner', 'admin'],
  'invite:view':   ['owner', 'admin'],
  'invite:create': ['owner', 'admin'],
  'invite:revoke': ['owner', 'admin'],
  'audit:view':    ['owner', 'admin'],
};

// ---------------------------------------------------------------------------
// Pure helper — no React, no store
// ---------------------------------------------------------------------------

/**
 * Returns true when `role` is allowed to perform `action`.
 * Default-deny: unknown action or null/undefined role → false.
 */
export function roleCan(role: MemberRole | null | undefined, action: string): boolean {
  if (!role) return false;
  const allowed = ACTION_ROLES[action as OrgAction];
  if (!allowed) return false;
  return (allowed as ReadonlyArray<string>).includes(role);
}

// ---------------------------------------------------------------------------
// Active-role accessor seam
// ---------------------------------------------------------------------------

/** Returns the current user's membership role in the active org, or null. */
export type ActiveRoleAccessor = () => MemberRole | null | undefined;

/** No-op stub — returns null until entities/organization wires the real store. */
const noopRoleAccessor: ActiveRoleAccessor = () => null;

let _activeRoleAccessor: ActiveRoleAccessor = noopRoleAccessor;

/**
 * Register a real active-role accessor.
 * Called once by entities/organization/model/org-store.ts during module evaluation.
 * Entities may import shared; shared must NEVER import entities.
 */
export function setActiveRoleAccessor(accessor: ActiveRoleAccessor): void {
  _activeRoleAccessor = accessor;
}

/**
 * Read the current active-org membership role via the registered accessor.
 * Returns null when no accessor has been registered or no org is active.
 */
export function getActiveRole(): MemberRole | null | undefined {
  return _activeRoleAccessor();
}

/**
 * Reset to the no-op stub.
 * Used in tests to isolate accessor state between test cases.
 */
export function resetActiveRoleAccessor(): void {
  _activeRoleAccessor = noopRoleAccessor;
}

// ---------------------------------------------------------------------------
// React hook — reads the active role and applies the mapping
// ---------------------------------------------------------------------------

/**
 * Returns true when the current user is allowed to perform `action` in the
 * active org. Returns false (default-deny) when:
 *   - no active org is set
 *   - the user has no membership role
 *   - the action is unknown
 */
export function useHasPerm(action: string): boolean {
  // Read the role synchronously from the accessor.
  // No subscription needed — callers re-render when the org store changes
  // (via OrgContextProvider / useOrgStore subscribers in the component tree).
  const role = getActiveRole();
  return roleCan(role, action);
}

// ---------------------------------------------------------------------------
// PermissionGuard component
// ---------------------------------------------------------------------------

export interface PermissionGuardProps {
  /** The action to check against the current membership role. */
  action: string;
  /** Rendered when the permission check passes. */
  children: ReactNode;
  /** Rendered when the permission check fails (default: null). */
  fallback?: ReactNode;
}

/**
 * Renders `children` only when the current user has the required permission.
 * Renders `fallback` (default null) otherwise.
 *
 * Default-deny: when no active org / membership / matching action → fallback.
 *
 * @example
 * <PermissionGuard action="org:update" fallback={<p>No access</p>}>
 *   <EditOrgForm />
 * </PermissionGuard>
 */
export function PermissionGuard({ action, children, fallback = null }: PermissionGuardProps): ReactNode {
  return useHasPerm(action) ? children : fallback;
}
